import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import moment from "moment";
import { toast } from "react-toastify";
import {
  getProjectActivities,
  getScopeBillingMilestones,
} from "../../../../services/scheduleService";
import { formattedAmountWithoutAed } from "../../../../utils/CommonFunction";
import { renderCurrencyIcon } from "../../../../utils/CurrencyUtils";
import { milestoneStatusStyleArray } from "../../config";
import { ReactComponent as PaymentIcon } from "../../../../assets/svg/PaymentIcon.svg";
import { ReactComponent as NewDesignIcon } from "../../../../assets/svg/NewDesignIcon.svg";
import { ReactComponent as NewRenovationIcon } from "../../../../assets/svg/NewRenovationIcon.svg";
import { ReactComponent as ArrowIcon } from "../../../../assets/svg/ArrowIcon.svg";

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
  padding: "1vh 1vw !important",
}));

const COLOR_BORDER = "#E5E7EB";
const COLOR_TEXT = "#0D0D0E";
const COLOR_SUBTEXT = "#646F86";
const COLOR_MUTED = "#949DAA";

const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return "-";
  return text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");
};

const getTagIcon = (tag) => {
  switch ((tag || "").toLowerCase()) {
    case "design":
      return <NewDesignIcon />;
    case "installation":
    case "renovation":
      return <NewRenovationIcon />;
    default:
      return <NewRenovationIcon />;
  }
};

const computeGroupAmount = (group) => {
  if (group?.milestone_amount != null && Number(group.milestone_amount) > 0) {
    return Number(group.milestone_amount);
  }
  if (Array.isArray(group?.linked_milestones)) {
    return group.linked_milestones.reduce(
      (acc, m) => acc + (Number(m?.total_cost) || 0),
      0
    );
  }
  return 0;
};

const Amount = ({ value, bold = false , isHeading= false}) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "2px",
      fontWeight: 500,
      color: COLOR_TEXT,
      whiteSpace: "nowrap",
    }}
  >
    {renderCurrencyIcon(isHeading ?  "black-small" : "black-xs-small")}
    {formattedAmountWithoutAed(Number(value) || 0)}
  </span>
);

const StatusIndicator = ({ status }) => {
  const statusBg =
    milestoneStatusStyleArray.find((style) => style.status === status)
      ?.backgroundColor || "#FFAD5C";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      <Box
        sx={{
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          border: "3px solid rgb(229, 229, 229)",
          backgroundColor: statusBg,
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontSize: "13px", color: COLOR_TEXT }}>
        {getStatusDisplayText(status)}
      </Typography>
    </Box>
  );
};

const TagWithIcon = ({ tag }) =>
  tag ? (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        color: COLOR_MUTED,
      }}
    >
      {getTagIcon(tag)}
      <Typography sx={{ fontSize: "13px", color: COLOR_MUTED }}>
        {tag}
      </Typography>
    </Box>
  ) : (
    <Typography sx={{ fontSize: "13px", color: COLOR_MUTED }}>-</Typography>
  );

const LinkedMilestonesTableDesktop = ({ milestones }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      paddingLeft: "1.5rem",
    }}
  >
    <ArrowIcon />
    <Table size="small" sx={{ width: "100%", marginTop: "1.5vh" }}>
      <TableHead>
        <TableRow>
          <StyledTableCell>Milestone Name</StyledTableCell>
          <StyledTableCell>Tags</StyledTableCell>
          <StyledTableCell>Start Date</StyledTableCell>
          <StyledTableCell>End Date</StyledTableCell>
          <StyledTableCell>Status</StyledTableCell>
          <StyledTableCell sx={{ textAlign: "right" }}>
            <span
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              Amount
            </span>
          </StyledTableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {milestones.map((m) => (
          <TableRow key={m.id}>
            <StyledTableCell>{m.milestone_name || "-"}</StyledTableCell>
            <StyledTableCell>
              <TagWithIcon tag={m.tags} />
            </StyledTableCell>
            <StyledTableCell>
              {m.start_date
                ? moment(m.start_date).format("MMM DD, YYYY")
                : "-"}
            </StyledTableCell>
            <StyledTableCell>
              {m.end_date ? moment(m.end_date).format("MMM DD, YYYY") : "-"}
            </StyledTableCell>
            <StyledTableCell>
              <StatusIndicator status={m.status} />
            </StyledTableCell>
            <StyledTableCell sx={{ textAlign: "right" }}>
              <Amount value={m.total_cost} />
            </StyledTableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const LinkedMilestoneCard = ({ milestone }) => (
  <Box
    sx={{
      backgroundColor: "#fff",
      padding: "16px",
      borderRadius: "8px",
      border: `1px solid ${COLOR_BORDER}`,
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.5,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: COLOR_TEXT,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {milestone.milestone_name || "-"}
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          <TagWithIcon tag={milestone.tags} />
        </Box>
      </Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography sx={{ fontSize: "12px", color: COLOR_MUTED }}>
          Amount
        </Typography>
        <Amount value={milestone.total_cost} />
      </Box>
    </Box>

    <Box sx={{ display: "flex", gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "12px", color: COLOR_MUTED }}>
          Start Date
        </Typography>
        <Typography sx={{ fontSize: "13px", color: COLOR_TEXT }}>
          {milestone.start_date
            ? moment(milestone.start_date).format("MMM DD, YYYY")
            : "-"}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "12px", color: COLOR_MUTED }}>
          End Date
        </Typography>
        <Typography sx={{ fontSize: "13px", color: COLOR_TEXT }}>
          {milestone.end_date
            ? moment(milestone.end_date).format("MMM DD, YYYY")
            : "-"}
        </Typography>
      </Box>
    </Box>

    <StatusIndicator status={milestone.status} />
  </Box>
);

const LinkedMilestonesView = ({ milestones, isMobile }) => {
  if (isMobile) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {milestones.map((m) => (
          <LinkedMilestoneCard key={m.id} milestone={m} />
        ))}
      </Box>
    );
  }
  return <LinkedMilestonesTableDesktop milestones={milestones} />;
};

const GroupRowDesktop = ({ group, hasLinked, expanded, onToggle, amount, actionButton }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "1vh 1vw",
      borderRadius: "8px",
      backgroundColor: "transparent",
      cursor: hasLinked ? "pointer" : "default",
      "&:hover": hasLinked && !expanded ? { backgroundColor: "#F9FAFB" } : {},
    }}
    onClick={hasLinked ? onToggle : undefined}
  >
    <Typography
      sx={{
        flex: 2,
        minWidth: 0,
        fontSize: "14px",
        color: COLOR_TEXT,
        fontWeight: 500,
      }}
    >
      {group.group_name || "-"}
    </Typography>
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        fontSize: "13px",
        color: COLOR_SUBTEXT,
      }}
    >
      Due Date
      <span style={{ fontSize: "14px", color: COLOR_TEXT, marginTop: "4px" }}>
        {group.due_date ? moment(group.due_date).format("MMM DD, YYYY") : "-"}
      </span>
    </Box>
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end ",
        textAlign: "right",
        fontSize: "13px",
        pr: 1,
        color: COLOR_SUBTEXT,
      }}
    >
      Amount
      <span style={{ fontSize: "14px", color: COLOR_TEXT, marginTop: "4px" }}>
 <Amount value={amount} />      </span>

    </Box>
    {actionButton ? (
      <Box sx={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
        {actionButton}
      </Box>
    ) : null}
    <IconButton
      size="small"
      sx={{
        visibility: hasLinked ? "visible" : "hidden",
        flexShrink: 0,
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (hasLinked) onToggle();
      }}
    >
      {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    </IconButton>
  </Box>
);

const GroupRowMobile = ({ group, hasLinked, expanded, onToggle, amount, actionButton }) => (
  <Box
    sx={{
      backgroundColor: "transparent",
      padding: "14px",
      border: `1px solid ${COLOR_BORDER}`,
      borderRadius: "8px",
      display: "flex",
      flexDirection: "column",
      gap: 1,
      cursor: hasLinked ? "pointer" : "default",
    }}
    onClick={hasLinked ? onToggle : undefined}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <div>
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: "14px",
          color: COLOR_TEXT,
          fontWeight: 500,
        }}
      >
        {group.group_name || "-"}
      </Typography>
       <Typography sx={{ fontSize: "12px", color: COLOR_SUBTEXT }}>
        Due Date:{" "}
        {group.due_date ? moment(group.due_date).format("MMM DD, YYYY") : "-"}
      </Typography>
      </div>
      <div>
        <Amount value={amount} />
      <IconButton
        size="small"
        sx={{
          visibility: hasLinked ? "visible" : "hidden",
          flexShrink: 0,
          p: 0.5,
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (hasLinked) onToggle();
        }}
      >
        {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>
      </div>
    </Box>
    {actionButton ? (
      <Box
        sx={{ display: "flex", justifyContent: "flex-end", pt: 0.5 }}
        onClick={(e) => e.stopPropagation()}
      >
        {actionButton}
      </Box>
    ) : null}
  </Box>
);

const memoActionButtonSx = {
  backgroundColor: "#4D6BF6",
  color: "#fff",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "12px",
  borderRadius: "6px",
  boxShadow: "none",
  padding: "0.5vh 0.7vw",
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: "#3B5BDB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#C7D0F9", color: "#fff" },
};

const BillingGroupRow = ({ group, isMobile, projectId, proposalId }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const hasLinked =
    Array.isArray(group?.linked_milestones) && group.linked_milestones.length > 0;
  const amount = computeGroupAmount(group);
  const handleToggle = () => setExpanded((v) => !v);

  // Delivery memo state (present only when the backend includes delivery_memos
  // on the billing milestone — gracefully degrades to "create" when absent).
  const memos = Array.isArray(group?.delivery_memos) ? group.delivery_memos : [];
  const latestMemo = memos[0] || null;
  const latestMemoStatus = String(latestMemo?.status || "").toUpperCase();
  // DRAFT (in progress) and REJECTED (sent back) are editable → contractor can
  // continue / re-submit. Anything else is view-only.
  const isEditableMemo =
    latestMemoStatus === "DRAFT" || latestMemoStatus === "REJECTED";
  const hasSubmittedMemo = latestMemo && !isEditableMemo;
  // Progress gating only makes sense for groups backed by activities. Initial
  // Deposit / Handover / Warranty have no linked milestones, and the backend
  // reports progress_complete: false for them simply because there is nothing
  // to average — gating on it would disable their CTA forever. Those are gated
  // on the due date instead: once it arrives, payment can be requested.
  const blockedByProgress = hasLinked && group?.progress_complete === false;
  const dueDateReached =
    !group?.due_date || !moment(group.due_date).startOf("day").isAfter(moment());
  const blockedByDueDate = !hasLinked && !dueDateReached;
  const ctaBlocked = blockedByProgress || blockedByDueDate;

  const routeProposalId = proposalId || projectId;

  const buildUrl = (extra = {}) => {
    const qs = new URLSearchParams({
      managed_by: "contractor",
      project_id: String(projectId),
      ...extra,
    });
    if (proposalId != null) qs.set("proposal_id", String(proposalId));
    // Path must use proposal_id so project details loads correctly on return.
    return `/project/details/${routeProposalId}/billing-milestones/${group.id}/our-delivery-memo?${qs.toString()}`;
  };

  const handleRequestPayment = () => {
    // Editing an existing DRAFT/REJECTED memo → carry its id so the screen
    // loads and updates it in place (rather than creating a new one).
    const extra =
      latestMemo?.id != null && isEditableMemo
        ? { memo_id: String(latestMemo.id) }
        : {};
    navigate(buildUrl(extra));
  };

  const handleViewMemo = () => {
    navigate(buildUrl({ memo_id: String(latestMemo.id), view: "1" }));
  };

  const requestLabel =
    latestMemoStatus === "DRAFT"
      ? "Continue Delivery Memo"
      : latestMemoStatus === "REJECTED"
      ? "Re-submit Delivery Memo"
      : "Request Payment";

  const canCreate = projectId != null && group?.id != null;
  const actionButton = !canCreate ? null : hasSubmittedMemo ? (
    <Button variant="contained" size="small" onClick={handleViewMemo} sx={memoActionButtonSx}>
      View Delivery Memo
    </Button>
  ) : (
    <Button
      variant="contained"
      size="small"
      onClick={handleRequestPayment}
      disabled={ctaBlocked}
      title={
        blockedByProgress
          ? "All activities must be 100% complete before creating a delivery memo"
          : blockedByDueDate
          ? `Available from ${moment(group.due_date).format("MMM DD, YYYY")}`
          : undefined
      }
      sx={memoActionButtonSx}
    >
      {requestLabel}
    </Button>
  );

  return (
    <Box
      sx={{
        mb: 1,
        borderRadius: "8px",
        backgroundColor: expanded && hasLinked ? "#FAFAFB" : "transparent",
        transition: "background-color 0.2s ease-in-out",
        overflow: "hidden",
      }}
    >
      {isMobile ? (
        <GroupRowMobile
          group={group}
          hasLinked={hasLinked}
          expanded={expanded}
          onToggle={handleToggle}
          amount={amount}
          actionButton={actionButton}
        />
      ) : (
        <GroupRowDesktop
          group={group}
          hasLinked={hasLinked}
          expanded={expanded}
          onToggle={handleToggle}
          amount={amount}
          actionButton={actionButton}
        />
      )}
      {hasLinked && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {isMobile ? (
            <Box sx={{ p: 1 }}>
              <LinkedMilestonesView
                milestones={group.linked_milestones}
                isMobile={isMobile}
              />
            </Box>
          ) : (
            <LinkedMilestonesView
              milestones={group.linked_milestones}
              isMobile={isMobile}
            />
          )}
        </Collapse>
      )}
    </Box>
  );
};

const ScopeBillingSection = ({ scope, projectId, proposalId, isMobile }) => {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Order Initial Deposit first, Warranty Completion last, the rest in
  // the order returned by the API.
  const sortedBillingMilestones = useMemo(() => {
    const list = Array.isArray(billing?.billing_milestones)
      ? [...billing.billing_milestones]
      : [];
    const rank = (g) => {
      const t = (g?.type || "").toUpperCase();
      const name = (g?.group_name || "").toLowerCase();
      if (t === "PROJECT_INITIAL_DEPOSIT" || name === "initial deposit") {
        return 0;
      }
      if (
        t === "PROJECT_WARRANTY_COMPLETION" ||
        name.includes("warranty period")
      ) {
        return 3;
      }
      if (t === "PROJECT_COMPLETION" || name.includes("handover")) {
        return 2;
      }
      return 1;
    };
    return list.sort((a, b) => rank(a) - rank(b));
  }, [billing?.billing_milestones]);

  const handleViewDetails = (e) => {
    e.stopPropagation();
    if (!proposalId || !scope?.scope_id) return;
    const qs = new URLSearchParams({
      scopeName: scope.scope_name || "",
      from: "billing-milestones",
    });
    if (projectId != null) qs.set("project_id", String(projectId));
    navigate(
      `/project/details/${proposalId}/scopes/${scope.scope_id}/activities-details?${qs.toString()}`,
    );
  };

  const fetchBilling = async () => {
    if (loaded || loading) return;
    setLoading(true);
    try {
      const response = await getScopeBillingMilestones(scope.scope_id);
      if (response?.success) {
        setBilling(response);
      } else {
        toast.error(response?.message || "Failed to load billing milestones");
      }
    } catch (error) {
      console.error("Error fetching billing milestones:", error);
      toast.error("Failed to load billing milestones");
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  const handleChange = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded) fetchBilling();
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      sx={{
        background: "#fff",
        borderRadius: "12px !important",
        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
        overflow: "hidden",
        "&:before": { display: "none" },
        mb: 2,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: "#f9fafb",
          padding: "0 16px",
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            justifyContent: "space-between",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
          <Typography sx={{ fontWeight: 500, fontSize: "16px" }}>
            {scope.scope_name || `Scope #${scope.scope_id}`}
          </Typography>
          {Array.isArray(scope.activities) && scope.activities.length > 0 && (
            <Typography sx={{ fontSize: "13px", color: "#667085" }}>
              {scope.activities.length} Activities
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex",  alignItems: "center", gap: 2, pr: 1 }}>
         
          <Button
            variant="contained"
            size="small"
            onClick={handleViewDetails}
            sx={{
              backgroundColor: "#4D6BF6",
              color: "#fff",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "12px",
              borderRadius: "6px",
              boxShadow: "none",
              padding: "0.5vh 0.7vw",
              "&:hover": {
                backgroundColor: "#3B5BDB",
                boxShadow: "none",
              },
            }}
          >
            View Details
          </Button>
           {billing?.total_amount != null && (
            <Amount value={billing.total_amount} bold isHeading />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: "#fff", p: 2 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : !billing ||
          !Array.isArray(billing.billing_milestones) ||
          billing.billing_milestones.length === 0 ? (
          <Typography
            sx={{ textAlign: "center", color: "#667085", py: 2 }}
          >
            No billing milestones found.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {sortedBillingMilestones.map((group) => (
              <BillingGroupRow
                key={group.group_id}
                group={group}
                isMobile={isMobile}
                projectId={projectId}
                proposalId={proposalId ?? billing?.proposal_id}
              />
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default function PaymentsBilling({ projectId, proposalId }) {
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (!projectId) return;
    loadActivities();
  }, [projectId]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const response = await getProjectActivities(projectId);
      if (response?.success) {
        setScopes(Array.isArray(response.data) ? response.data : []);
      } else {
        toast.error(response?.message || "Failed to load activities");
      }
    } catch (error) {
      console.error("Error loading activities:", error);
      toast.error("Failed to load activities");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 6,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!scopes.length) {
    return (
      <Box sx={{ textAlign: "center", py: 4, color: COLOR_SUBTEXT }}>
        No activities found.
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0, p: 1 }}>
      {scopes.map((scope) => (
        <ScopeBillingSection
          key={scope.scope_id}
          scope={scope}
          projectId={projectId}
          proposalId={proposalId}
          isMobile={isMobile}
        />
      ))}
    </Box>
  );
}
